import React, { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { NavLink, useLocation } from 'react-router-dom';
import { useHistory } from '@useHistory/useHistoryCompat';
import useService from '../../../injector/hooks/useService';
import { useActiveTheme } from '../../../theming/hooks/themeProvider';
import AuthAPI from '../../../auth/api/AuthAPI';
import { ACCOUNT_EXEC_SCHEDULE_CALL_LINK, CHANGE_LOG_URL } from '../../../core/constants/constants';
import {
  MultiRegionService,
  ClusterMetadata
} from '../../../auth/services/multi-region.service';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import { MultiRegionTracking } from '../../tracking/multiRegionDropdownTracking';
import { ShortcutsDialogHandlerReactService } from '../../../a11y/shortcuts/service/shortcuts-dialog-handler-react.service';
import { HeadwayService } from '../../../core/services/headway.service';
import { NotificationService } from '../../../core/services/notification.service';
import { TeamTrialStatus } from '../../../core/interfaces/trial-details';
import { TrialDetailsService } from '../../../core/services/trial-details.service';
import { getDaysFromTimestamp, getHoursFromTimestamp } from '../../../date-time/utils/date';
import { AuthService } from '../../../auth/services/auth.service';
import HdShortcut from '../../../a11y/shortcuts/components/shortcut/HdShortcut';
import styles from './Topbar.module.scss';
import { Icon } from '@hevoio/hevo-ui-toolkit';
import {
  HdButton,
  HdCalendar,
  HdIconButton,
  HdMenu,
  HdMenuItem,
  HdTooltip
} from '../../../components';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import HdSwitchWrapper from '../../../switch/components/switch-wrapper/HdSwitchWrapper';
import { ColorMode } from '../../../core/interfaces/color-mode';
import { ColorModeService } from '../../../core/services/color-mode.service';
import { getDocLink } from '../../../docs/utils/docLinkHandler';
import { getUnreadDisplayCount } from '../../../core/utils/string';
import { TRIAL_NOT_STARTED_TOOLTIP } from '../../constants/strings';
import { numFormatter } from '../../../core/utils/number';
import useHasPermission from '../../../access-control/hooks/useHasPermission';
import useUserService from '../../../auth/hooks/useUserService';
import { RbacPermissions } from '../../../core/interfaces/user';
import useSettingsStorageService from '../../../auth/hooks/useSettingsStorageService';
import useTeamSettings from '../../../containers/team/hooks/useTeamSettingsService';
import { SyncFrequencyGTM } from '../../../containers/pipeline/sync-frequency-migration/components/sync-frequency-gtm/SyncFrequencyGTM';
import { SYNC_FREQUENCY_GTM_SHOWN_KEY } from '../../../containers/pipeline/sync-frequency-migration/constant';
import { isUserEligibleToStartSyncFrequencyMigration } from '../../../containers/pipeline/sync-frequency-migration/utils/utils';
import { GlobalSearchButton } from './components/GlobalSearchButton/GlobalSearchButton';
import { IconPlanet, IconWorld, IconClockHour3, IconCalendarUser, IconCalendarClock, IconBell, IconChevronDown, IconBook, IconSparkles, IconKeyboard, IconUser, IconUsers, IconChartLine, IconCreditCard, IconMoon, IconLogin } from '@tabler/icons-react';

export const dataIdGenerator = getDataIdGenerator('Topbar');

export default function TopBar() {
  const { eventTrack } = useAnalyticsTracker();
  const history = useHistory();
  const location = useLocation();
  const {hasPermission: isInternalUserOrHasPermission} = useHasPermission();
  const { user } = useUserService();
  const { getSettings, applySettings } = useSettingsStorageService();
  const { getTeamSettings } = useTeamSettings();

  const userService = useUserService();

  const authService = useService(AuthService);
  const multiRegionService = useService(MultiRegionService);
  const headwayService = useService(HeadwayService);
  const notificationService = useService(NotificationService);
  const trialDetailsService = useService(TrialDetailsService);
  const shortcutsDialogHandlerReactService = useService(ShortcutsDialogHandlerReactService);
  const colorModeService = useService(ColorModeService);

  const [showUserAccountMenu, setShowUserAccountMenu] = useState(false);
  const [showScheduleCallDisplay, setShowScheduleCallDisplay] = useState(false);
  const [showRegionsMenu, setShowRegionsMenu] = useState(false);
  const [showMiscMenu, setShowMiscMenu] = useState(false);
  const [showRegionSwitcher, setShowRegionSwitcher] = useState(false);
  const [regionGroups, setRegionGroups] = useState<
    { provider: string; options: ClusterMetadata[] }[]
  >([]);
  const [selectedRegion, setSelectedRegion] = useState<ClusterMetadata>(null);
  const [newFeatsUnseenCount, setNewFeatsUnseenCount] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [trialInfo, setTrialInfo] = useState({
    trialStartedText: '',
    showTrialStartedBadge: false,
    showTrialNotStartedBadge: false,
    trialEndTimestamp: 0
  });
  const [showSyncFrequencyGTM, setShowSyncFrequencyGTM] = useState(false);

  const theme  = useActiveTheme();

  useEffect(() => {
    headwayService.init();
    const headwaySubscription = headwayService.unreadCountSubject.subscribe((count: number) => {
      setNewFeatsUnseenCount(count);
    });

    const storeSubscription = notificationService.unreadCount$.subscribe(unreadCount => {
      setNewCount(unreadCount.regular + unreadCount.enterprise);
    });

    const notificationSubscription = notificationService
      .getNotificationCountObservable()
      .subscribe();

    const trialDetailsSubscription = getTrialDetails();

    openSyncFrequencyGTMModalByDefault();

    return () => {
      headwaySubscription.unsubscribe();
      notificationSubscription.unsubscribe();
      storeSubscription.unsubscribe();
      trialDetailsSubscription.unsubscribe();
    };
  }, []);

  const getTrialDetails = () =>
    trialDetailsService.trialDetails$.subscribe(
      details => {
        setShowScheduleCallDisplay(details.isInTrial());
        setMultiRegionStatus();

        if (details.trialEndTs && details.status === TeamTrialStatus.CREATED) {
          const trialPeriodLeft = getTrialPeriodLeft(details.trialEndTs);

          setTrialInfo({
            showTrialStartedBadge: true,
            showTrialNotStartedBadge: false,
            trialEndTimestamp: details.trialEndTs,
            trialStartedText: getTrialStatusMessage(trialPeriodLeft)
          });
        } else if (details.status === TeamTrialStatus.SENT) {
          setTrialInfo(prev => ({
            ...prev,
            showTrialStartedBadge: false,
            showTrialNotStartedBadge: true
          }));
        } else {
          setTrialInfo(prev => ({
            ...prev,
            showTrialStartedBadge: false,
            showTrialNotStartedBadge: false
          }));
        }
      },
      () => {
        setMultiRegionStatus();
      }
    );

  const getTrialPeriodLeft = trialEndTimestamp => trialEndTimestamp - new Date().getTime();

  const getTrialStatusMessage = trialPeriodLeft => {
    const message = 'Trial Expires in';
    const daysLeft = getDaysFromTimestamp(trialPeriodLeft);
    if (daysLeft > 1) {
      return `${message} ${Math.ceil(daysLeft)} days`;
    }

    const hoursLeft = getHoursFromTimestamp(trialPeriodLeft);
    if (hoursLeft > 1) {
      return `${message} ${Math.ceil(hoursLeft)} hours`;
    }

    return `${message} less than 1 hour`;
  };

  const setMultiRegionStatus = () => {
    if (
      multiRegionService.isMultiRegionUser() &&
      multiRegionService.getMultiRegionsValue()?.length > 1 &&
      multiRegionService.getSelectedRegionValue()
    ) {
      setShowRegionSwitcher(true);
      setRegionGroups(multiRegionService.getRegionsGroupedByProvider());
      setSelectedRegion(multiRegionService.getSelectedRegionValue());
    }
  };

  const getDefaultMultiRegionTrackingPayload = () => ({
    currentCluster: multiRegionService.getSelectedRegionValue()?.id
  });

  const logout = () => {
    authService.logout(false).subscribe();
  };

  const markAllNewSeen = () => {
    eventTrack({
      action: '[Topbar] changelog page link click'
    });

    headwayService.markAllSeen();
  };

  const showSearchDialog = () => {
    shortcutsDialogHandlerReactService.openGlobalSearchDialog();
  };

  const selectRegion = option => {
    if (!option.current_cluster) {
      eventTrack({
        action: MultiRegionTracking.MULTI_REGION_REGION_CHANGED,
        properties: {
          ...getDefaultMultiRegionTrackingPayload(),
          targetCluster: option.id
        }
      });

      setSelectedRegion(option);

      AuthAPI.getRegionSwitchingToken(option.id).then(res => {
        window.location.href = `${option.url}?session_token=${res.data?.temp_session_token}`;
      });
    }
  };

  const onScheduleCallClick = () => {
    window.open(ACCOUNT_EXEC_SCHEDULE_CALL_LINK, '_blank');

    eventTrack({
      action: '[Free Trial]: Talk to us button clicked',
      properties: {
        section: 'topbar'
      }
    });
  };

  const openAlertDrawer = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('drawer', 'alerts');

    history.push({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`
    });
  };

  const trackMultiRegionEvent = action => {
    eventTrack({
      action,
      properties: getDefaultMultiRegionTrackingPayload()
    });
  };

  const showShortcutDialog = () => shortcutsDialogHandlerReactService.openKeyboardShortcutDialog();

  const toggleColorMode = () => colorModeService.toggleColorMode();

  const alertCount = useMemo(() => getUnreadDisplayCount(newCount), [newCount]);
  const newUnseenFeaturesCount = useMemo(
    () => numFormatter(newFeatsUnseenCount),
    [newFeatsUnseenCount]
  );

  const isSyncFrequencyGTMEnabled =
    isUserEligibleToStartSyncFrequencyMigration(userService.rbacRoles) &&
    !!getTeamSettings()?.show_sync_frequency_migration_popup &&
    !getTeamSettings()?.use_sync_execution_policy;

  const openSyncFrequencyGTMModalByDefault = () => {
    if (!getSettings(SYNC_FREQUENCY_GTM_SHOWN_KEY) && isSyncFrequencyGTMEnabled) {
      setShowSyncFrequencyGTM(true);
      applySettings(SYNC_FREQUENCY_GTM_SHOWN_KEY, true);
    }
  };

  return (
    <div className={styles.topBar} data-id={dataIdGenerator('')}>
      <div className='center-flex-row flex-1'>
        <GlobalSearchButton dataId={dataIdGenerator('')} onClick={showSearchDialog} />
      </div>

      {showRegionSwitcher ? (
        <>
          <HdTooltip title='Hevo Account Region' dataId={dataIdGenerator('region-switcher-tooltip')}>
            <button
              data-id='region-switcher'
              type='button'
              onClick={() => {
                trackMultiRegionEvent(MultiRegionTracking.MULTI_REGION_DROPDOWN_CLICK);
                setShowRegionsMenu(prev => !prev);
              }}
              id='regionsMenu'
              className={clsx('mr-2', styles.dropdownTrigger)}
            >
              <Icon SVGIcon={IconWorld} size={2} className='mr-1' />
              {selectedRegion?.display_name}
              <Icon SVGIcon={IconChevronDown} size={2} className={styles.dropdownTriggerIcon} />
            </button>
          </HdTooltip>

          <HdMenu
            target='#regionsMenu'
            open={showRegionsMenu}
            onClose={() => setShowRegionsMenu(false)}
          >
            {regionGroups.map(regionGroup => (
              <React.Fragment key={regionGroup.provider}>
                <div className={clsx(styles.regionGroupContent, 'text-uppercase')}>
                  {regionGroup.provider}
                </div>

                {regionGroup.options.map(region => (
                  <HdMenuItem
                    selected={selectedRegion?.id === region.id}
                    onClick={e => {
                      e.stopPropagation();
                      selectRegion(region);
                      setShowRegionsMenu(false);
                    }}
                    dataId={dataIdGenerator(region.id)}
                    key={region.id}
                  >
                    {region.display_name}
                  </HdMenuItem>
                ))}
              </React.Fragment>
            ))}
          </HdMenu>
        </>
      ) : null}

      {trialInfo.showTrialNotStartedBadge ? (
        <HdTooltip title={TRIAL_NOT_STARTED_TOOLTIP} dataId={dataIdGenerator('trial-not-started-tooltip')}>
          <div
            className='hd-outline-label hd-outline-label-thumbnail-left mr-4'
            id='trial-not-started'
            data-id={dataIdGenerator('trial-not-started')}
          >
            <Icon SVGIcon={IconClockHour3} size={2} className='hd-outline-label-thumbnail' />
            Trial has not started
          </div>
        </HdTooltip>
      ) : null}

      {trialInfo.showTrialStartedBadge && (
        <HdTooltip title={<HdCalendar value={trialInfo.trialEndTimestamp} />} dataId='trialStarted'>
          <div
            className='hd-faded-label hd-faded-label-deferred hd-faded-label-bordered hd-faded-label-thumbnail-left mr-4'
            id='trial-status'
            data-id={dataIdGenerator('trial-status')}
          >
            <Icon SVGIcon={IconCalendarClock} size={2} className='hd-faded-label-thumbnail' />
            {trialInfo.trialStartedText}
          </div>
        </HdTooltip>
      )}

      {showScheduleCallDisplay && (
        <HdButton
          dataId={dataIdGenerator('scheduleCall')}
          variation='outline'
          size='sm'
          className='btn-thumbnail-left'
          onClick={onScheduleCallClick}
        >
          <Icon SVGIcon={IconCalendarUser} size={2} className='thumb' />
          Schedule a demo
        </HdButton>
      )}

      {isSyncFrequencyGTMEnabled && (
        <HdIconButton
          dataId={dataIdGenerator('syncFrequency')}
          onClick={() => setShowSyncFrequencyGTM(prev => !prev)}
          className={clsx('ml-1', styles.syncFrequencyIcon)}
        >
          <Icon SVGIcon={IconCalendarClock} size={2} />
        </HdIconButton>
      )}

      <HdTooltip title='Quick Links' dataId={dataIdGenerator('quick-links-tooltip')}>
        <HdIconButton
          dataId={dataIdGenerator('tour-links')}
          onClick={() => setShowMiscMenu(prev => !prev)}
          id='misc'
          className='ml-1'
        >
          <Icon SVGIcon={IconPlanet} size={3} />
        </HdIconButton>
      </HdTooltip>

      <HdMenu target='#misc' open={showMiscMenu} onClose={() => setShowMiscMenu(false)}>
        <HdMenuItem
          component='a'
          rel='noreferrer'
          dataId={dataIdGenerator('documentation')}
          href={getDocLink('/', 'top_bar')}
          target='_blank'
          onClick={() => setShowMiscMenu(false)}
        >
          <Icon SVGIcon={IconBook} size={2} className='mr-3' />
          Documentation
        </HdMenuItem>

        <HdMenuItem
          component='a'
          rel='noreferrer'
          dataId={dataIdGenerator('new-documentation')}
          href={CHANGE_LOG_URL}
          target='_blank'
          onClick={() => {
            markAllNewSeen();
            setShowMiscMenu(false);
          }}
        >
          <Icon SVGIcon={IconSparkles} size={2} className='mr-3' />
          What's New
          {newFeatsUnseenCount ? (
            <span className='badge overlay-badge overlay-badge-primary ml-2'>
              {newUnseenFeaturesCount}
            </span>
          ) : null}
        </HdMenuItem>

        <HdMenuItem onClick={() => {
          showShortcutDialog();
          setShowMiscMenu(false);
        }} dataId='keyboard-shortcut'>
          <Icon SVGIcon={IconKeyboard} size={2} className='mr-3' />
          Keyboard Shortcuts
          <span className={styles.menuItemShortcut}>
            <HdShortcut command='global.keyboardShortcuts' />
          </span>
        </HdMenuItem>
      </HdMenu>

      <HdTooltip title='Quick Alerts' dataId={dataIdGenerator('quick-alerts-tooltip')}>
        <HdIconButton
          dataId={dataIdGenerator('quick-alerts')}
          className='ml-1 mr-2'
          onClick={openAlertDrawer}
        >
          <span
            className={clsx(
              !newCount && 'overlay-badge-container-hidden',
              'overlay-badge-container overlay-badge-container-overlap'
            )}
            data-id='alerts-icon'
          >
            <Icon size={3} SVGIcon={IconBell} />
            <span className={clsx('badge overlay-badge-error overlay-badge hd-badge-active')} data-id='alerts-badge'>
              {alertCount}
            </span>
          </span>
        </HdIconButton>
      </HdTooltip>

      <div className='h-100'>
        <button
          data-id={dataIdGenerator('user-details-button')}
          type='button'
          className={styles.userDetails}
          id='userAccountMenu'
          onClick={() => setShowUserAccountMenu(prev => !prev)}
        >
          <div className={styles.userProfileImgContainer}>
            <img
              src={
                user?.profileImage ||
                'https://res.cloudinary.com/hevo/image/upload/v1661186304/dashboard/profile-image-default_oeutti.svg'
              }
              alt='account-avatar'
              data-id='account-avatar'
            />
          </div>

          <div className={styles.nameEmailWrapper}>
            <div className='center-flex-row'>
              <HdTooltip title={user?.name} enableOnTextOverflow dataId='user-name'>
                <span className={styles.userName}>{user?.name}</span>
              </HdTooltip>

              <Icon SVGIcon={IconChevronDown} className='ml-1' size={3} />
            </div>

            {user?.name !== user?.email ? (
              <HdTooltip title={user?.email} enableOnTextOverflow dataId='user-email'>
                <span className={styles.userEmail}>{user?.email}</span>
              </HdTooltip>
            ) : null}
          </div>
        </button>
      </div>

      <div className='d-none' id='headway_widget_trigger' />

      <HdMenu
        target='#userAccountMenu'
        open={showUserAccountMenu}
        onClick={e => {
          e.stopPropagation();
          setShowUserAccountMenu(false);
        }}
        menuListClassName={styles.navMenu}
        onClose={() => setShowUserAccountMenu(false)}
      >
        <HdMenuItem component={NavLink} to='/account' dataId={dataIdGenerator('account')}>
          <Icon SVGIcon={IconUser} size={2} className='mr-3' />

          <span className='tab-nav-link-text'>Account</span>
        </HdMenuItem>

        <HdMenuItem component={NavLink} to='/team' dataId={dataIdGenerator('team')}>
          <Icon SVGIcon={IconUsers} size={2} className='mr-3' />

          <span className='tab-nav-link-text'>Team</span>

          <span className={styles.menuItemShortcut}>
            <HdShortcut command='global.team' />
          </span>
        </HdMenuItem>

        {!isInternalUserOrHasPermission(RbacPermissions.BILLING_EDIT) && (
          <HdMenuItem
            component={NavLink}
            to='/payment/usage-summary'
            dataId={dataIdGenerator('usage-summary')}
          >
            <Icon SVGIcon={IconChartLine} size={2} className='mr-3' />

            <span className='tab-nav-link-text'>Usage Summary</span>
          </HdMenuItem>
        )}

        {isInternalUserOrHasPermission(RbacPermissions.BILLING_EDIT) && (
          <HdMenuItem component={NavLink} to='/payment' dataId={dataIdGenerator('payment')}>
            <Icon SVGIcon={IconCreditCard} size={2} className='mr-3' />

            <span className='tab-nav-link-text'>Billing</span>

            <span className={styles.menuItemShortcut}>
              <HdShortcut command='global.payment' />
            </span>
          </HdMenuItem>
        )}

        <HdMenuItem component={NavLink} to='/alerts/preferences' dataId={dataIdGenerator('alerts')}>
          <Icon SVGIcon={IconBell} size={2} className='mr-3' />

          <span className='tab-nav-link-text'>Alerts</span>
        </HdMenuItem>

        <HdMenuItem
          sx={{ borderTop: 'solid var(--border-width) var(--divider-color)' }}
          onClick={e => {
            e.stopPropagation();
            toggleColorMode();
          }}
          dataId={dataIdGenerator('color-toggle')}
        >
          <span className='center-flex-row'>
            <Icon SVGIcon={IconMoon} size={2} className='mr-3' />
            Dark Mode
          </span>

          <HdSwitchWrapper
            className='ml-4 d-inline-block'
            id='color-mode'
            dataId={dataIdGenerator('color-mode')}
            label=''
            onChange={toggleColorMode}
            switchProps={{
              checked: theme === ColorMode.DARK
            }}
          />
        </HdMenuItem>

        <HdMenuItem
          sx={{ borderTop: 'solid var(--border-width) var(--divider-color)' }}
          onClick={logout}
          dataId={dataIdGenerator('logout')}
        >
          <span className='text-error center-flex-row'>
            <Icon SVGIcon={IconLogin} size={2} className='mr-3' />
            Log out
          </span>
        </HdMenuItem>
      </HdMenu>

      <SyncFrequencyGTM
        open={showSyncFrequencyGTM}
        onClose={() => {
          setShowSyncFrequencyGTM(false);
        }}
      />
    </div>
  );
}
